import { useCallback, useState } from 'react';
import { useTranslation } from 'next-i18next';
import CloseIcon from 'components/icon/close-icon';
import { Drawer, DrawerSnapPoint, SNAP_DURATION_ONE_STEP } from 'components/atomic/organisms/drawer';
import { DrawerHeader } from 'components/atomic/molecules/drawer-header';
import { Namespaces, LOCALE_SEARCH_PATHS as P } from 'types/locales';
import type { Filter } from 'contexts/preferences/listing-params/types';
import { SavedSearchPanelContent } from './saved-search-panel-content';
import type { MapBounds } from 'domain/saved-search';
import styles from './mobile-saved-search-sheet.module.scss';

export type MobileSavedSearchSheetProps = {
  onClose: () => void;
  /** The search params currently applied on the page — persisted when the user saves. */
  currentFilter?: Filter;
  /** The map's rectangle, recorded on a search saved with no area slug to locate it. */
  mapBounds?: MapBounds | null;
  /** True on a page where the map is the search; the saved search drops any slug. */
  locatedByMap?: boolean;
  /** Called when the user picks "All Saved Searches". */
  onViewAllSavedSearches?: () => void;
};

const SHEET_HEIGHT_RATIO = 0.85;
const FALLBACK_VIEWPORT_HEIGHT = 800;

/**
 * Mobile swipe-up equivalent of the desktop `SavedSearchPanel` popover. Reuses the
 * same `SavedSearchPanelContent` — only the chrome around it (a `Drawer` instead of
 * an MUI `Popover`) differs. The caller mounts this only while the sheet should be
 * open, so it always starts fresh at `DrawerSnapPoint.Full`.
 */
export function MobileSavedSearchSheet({ onClose, currentFilter, mapBounds, locatedByMap, onViewAllSavedSearches }: MobileSavedSearchSheetProps) {
  const { t } = useTranslation(Namespaces.SEARCH);
  const [viewportHeight] = useState(() => (
    typeof window === 'undefined' ? FALLBACK_VIEWPORT_HEIGHT : window.innerHeight
  ));
  const [drawerState, setDrawerState] = useState<DrawerSnapPoint>(DrawerSnapPoint.Full);

  const handleStateChange = useCallback((newState: DrawerSnapPoint) => {
    setDrawerState(newState);
    if (newState !== DrawerSnapPoint.Full) {
      // Let the slide-down transition finish before unmounting the sheet.
      window.setTimeout(onClose, SNAP_DURATION_ONE_STEP);
    }
  }, [onClose]);

  // Backdrop-tap and the header close button go through the same animated path as
  // a swipe-down gesture, instead of calling `onClose` directly and skipping the
  // slide-down transition.
  const handleClose = useCallback(() => {
    handleStateChange(DrawerSnapPoint.Minimized);
  }, [handleStateChange]);

  const fullHeight = Math.round(viewportHeight * SHEET_HEIGHT_RATIO);

  return (
    <Drawer
      currentState={drawerState}
      onStateChange={handleStateChange}
      minimizedHeight={0}
      partialHeight={0}
      fullHeight={fullHeight}
      enableGestures
      onBackdropClick={handleClose}
      zIndex={1100}
      header={
        <DrawerHeader>
          <h2 className={styles.title}>{t(P.SAVED_SEARCH_PANEL_TITLE, { defaultValue: 'Saved Searches' })}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={t(P.SAVED_SEARCH_PANEL_CLOSE, { defaultValue: 'Close' })}
          >
            <CloseIcon className={styles.closeIcon} />
          </button>
        </DrawerHeader>
      }
    >
      <SavedSearchPanelContent onClose={onClose} currentFilter={currentFilter} mapBounds={mapBounds} locatedByMap={locatedByMap} onViewAllSavedSearches={onViewAllSavedSearches} />
    </Drawer>
  );
}
